import { useState, useMemo } from 'react';
import {
  useSuggestions,
  SUGGESTION_CATEGORIES,
  SUGGESTION_STATUS_META,
  type SuggestionCategory,
  type SuggestionStatus,
  type Suggestion,
} from '../../hooks/useSuggestions';

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });

const fmtDatetime = (d: string) =>
  new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

type Tab     = 'publica' | 'nao_aprovadas' | 'minhas';
type SortKey = 'votos' | 'recentes' | 'desenvolvendo';

const STATUS_FILTERS: { value: SuggestionStatus | 'all'; label: string }[] = [
  { value: 'all',               label: 'Todas' },
  { value: 'em_analise',        label: 'Em análise' },
  { value: 'aprovada',          label: 'Aprovadas' },
  { value: 'planejada',         label: 'Planejadas' },
  { value: 'em_desenvolvimento', label: 'Desenvolvendo' },
  { value: 'concluida',         label: 'Concluídas' },
];

const SORT_OPTIONS: { key: SortKey; label: string; icon: string }[] = [
  { key: 'votos',       label: 'Mais votadas',       icon: 'ri-thumb-up-line' },
  { key: 'recentes',    label: 'Mais recentes',       icon: 'ri-time-line' },
  { key: 'desenvolvendo', label: 'Em desenvolvimento', icon: 'ri-code-line' },
];

// Status progression timeline
const STATUS_FLOW: SuggestionStatus[] = [
  'em_analise', 'aprovada', 'planejada', 'em_desenvolvimento', 'concluida',
];

const getStatusProgress = (status: SuggestionStatus): number => {
  const idx = STATUS_FLOW.indexOf(status);
  return idx === -1 ? 0 : idx;
};

export default function SugestoesPage() {
  const { publicSuggestions, mySuggestions, loading, createSuggestion, vote, downvote } = useSuggestions();

  const [tab, setTab]               = useState<Tab>('publica');
  const [formOpen, setFormOpen]     = useState(false);
  const [filterCat, setFilterCat]   = useState('all');
  const [filterStatus, setFilterStatus] = useState<SuggestionStatus | 'all'>('all');
  const [sortKey, setSortKey]       = useState<SortKey>('votos');
  const [detailSuggestion, setDetailSuggestion] = useState<Suggestion | null>(null);

  const rejectedSuggestions = mySuggestions.filter(s => s.status === 'recusada');

  const publicVisible = useMemo(() => {
    let list = publicSuggestions.filter(s => {
      if (filterCat !== 'all' && s.category !== filterCat) return false;
      if (filterStatus !== 'all' && s.status !== filterStatus) return false;
      return true;
    });

    if (sortKey === 'votos') {
      list = [...list].sort((a, b) => b.votes_count - a.votes_count);
    } else if (sortKey === 'recentes') {
      list = [...list].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    } else if (sortKey === 'desenvolvendo') {
      list = [...list].sort((a, b) => {
        const aIsDev = a.status === 'em_desenvolvimento' ? -1 : 0;
        const bIsDev = b.status === 'em_desenvolvimento' ? -1 : 0;
        return aIsDev - bIsDev;
      });
    }
    return list;
  }, [publicSuggestions, filterCat, filterStatus, sortKey]);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-[20px] font-700 text-gray-900">Sugestões</h1>
            <span className="text-[11px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-600">Beta</span>
          </div>
          <p className="text-[13px] text-gray-400">Vote nas melhores ideias e ajude a definir o que construímos</p>
        </div>
        <button
          onClick={() => setFormOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-600 rounded-xl text-[13px] cursor-pointer whitespace-nowrap transition-colors"
        >
          <i className="ri-add-line" />
          Nova sugestão
        </button>
      </div>

      {/* Info banner */}
      <div className="px-3 py-2.5 bg-amber-50 border border-amber-100 rounded-xl flex items-center gap-2.5">
        <i className="ri-information-line text-amber-500 text-[13px] shrink-0" />
        <p className="text-[12px] text-amber-700">
          Sugestões são publicadas para todos. O autor permanece <strong>anônimo</strong>. As mais votadas são priorizadas pelo time.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {([
          { key: 'publica',       label: 'Todas as sugestões', icon: 'ri-global-line',       badge: null },
          { key: 'nao_aprovadas', label: 'Não aprovadas',      icon: 'ri-close-circle-line', badge: rejectedSuggestions.length },
          { key: 'minhas',        label: 'Minhas',             icon: 'ri-user-line',          badge: mySuggestions.length },
        ] as const).map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-600 transition-all cursor-pointer whitespace-nowrap ${
              tab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <i className={t.icon} />
            {t.label}
            {(t.badge !== null && t.badge > 0) && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                t.key === 'nao_aprovadas' ? 'bg-gray-200 text-gray-600' : 'bg-emerald-100 text-emerald-700'
              }`}>
                {t.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Filters + sort (public tab only) */}
      {tab === 'publica' && (
        <div className="space-y-2.5">
          {/* Sort row */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[11px] text-gray-400 font-600 mr-0.5">Ordenar:</span>
            {SORT_OPTIONS.map(opt => (
              <button
                key={opt.key}
                onClick={() => setSortKey(opt.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[12px] font-600 cursor-pointer whitespace-nowrap transition-all border ${
                  sortKey === opt.key
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                }`}
              >
                <i className={`${opt.icon} text-[11px]`} />
                {opt.label}
              </button>
            ))}
          </div>

          {/* Category row */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[11px] text-gray-400 font-600 mr-0.5">Cat:</span>
            <button
              onClick={() => setFilterCat('all')}
              className={`px-2.5 py-1 rounded-lg text-[11px] font-500 cursor-pointer whitespace-nowrap transition-colors ${
                filterCat === 'all' ? 'bg-gray-800 text-white' : 'bg-white border border-gray-200 text-gray-500 hover:bg-gray-50'
              }`}
            >
              Todas
            </button>
            {SUGGESTION_CATEGORIES.map(cat => (
              <button
                key={cat.value}
                onClick={() => setFilterCat(cat.value === filterCat ? 'all' : cat.value)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-500 cursor-pointer whitespace-nowrap transition-colors ${
                  filterCat === cat.value ? 'bg-gray-800 text-white' : 'bg-white border border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}
              >
                <i className={`${cat.icon} text-[10px]`} />
                {cat.label}
              </button>
            ))}
          </div>

          {/* Status row */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[11px] text-gray-400 font-600 mr-0.5">Status:</span>
            {STATUS_FILTERS.map(sf => (
              <button
                key={sf.value}
                onClick={() => setFilterStatus(sf.value === filterStatus ? 'all' : sf.value)}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-500 cursor-pointer whitespace-nowrap transition-colors ${
                  filterStatus === sf.value ? 'bg-emerald-600 text-white' : 'bg-white border border-gray-200 text-gray-500 hover:bg-gray-50'
                }`}
              >
                {sf.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-44 bg-gray-100 rounded-2xl animate-pulse" />)}
        </div>
      ) : tab === 'publica' ? (
        publicVisible.length === 0 ? (
          <EmptyState icon="ri-lightbulb-line" title="Nenhuma sugestão encontrada" desc="Tente outros filtros ou seja o primeiro a sugerir algo." />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {publicVisible.map(s => (
              <SuggestionCard key={s.id} suggestion={s} onVote={vote} onDownvote={downvote} onOpen={() => setDetailSuggestion(s)} />
            ))}
          </div>
        )
      ) : tab === 'nao_aprovadas' ? (
        rejectedSuggestions.length === 0 ? (
          <EmptyState icon="ri-checkbox-circle-line" title="Nenhuma sugestão não aprovada" desc="Suas sugestões ainda não foram avaliadas ou todas foram aprovadas." />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {rejectedSuggestions.map(s => (
              <SuggestionCard key={s.id} suggestion={s} onVote={vote} onDownvote={downvote} isOwn showModNotes onOpen={() => setDetailSuggestion(s)} />
            ))}
          </div>
        )
      ) : (
        mySuggestions.length === 0 ? (
          <EmptyState icon="ri-draft-line" title="Você ainda não enviou nenhuma sugestão" desc="Clique em 'Nova sugestão' e compartilhe sua ideia com o time." />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {mySuggestions.map(s => (
              <SuggestionCard key={s.id} suggestion={s} onVote={vote} onDownvote={downvote} isOwn onOpen={() => setDetailSuggestion(s)} />
            ))}
          </div>
        )
      )}

      {formOpen && (
        <SuggestionFormModal onClose={() => setFormOpen(false)} onCreate={createSuggestion} />
      )}

      {detailSuggestion && (
        <SuggestionDetailModal
          suggestion={detailSuggestion}
          onClose={() => setDetailSuggestion(null)}
          onVote={(id) => { vote(id); setDetailSuggestion(prev => prev ? { ...prev, user_has_voted: !prev.user_has_voted, votes_count: prev.user_has_voted ? prev.votes_count - 1 : prev.votes_count + 1 } : null); }}
          onDownvote={(id) => { downvote(id); setDetailSuggestion(prev => prev ? { ...prev, user_has_downvoted: !prev.user_has_downvoted, downvotes_count: (prev.downvotes_count ?? 0) + (prev.user_has_downvoted ? -1 : 1) } : null); }}
        />
      )}
    </div>
  );
}

// ── Empty State ──────────────────────────────────────────────────────────────
const EmptyState = ({ icon, title, desc }: { icon: string; title: string; desc: string }) => (
  <div className="bg-white border border-gray-100 rounded-xl py-10 flex flex-col items-center text-center">
    <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center mb-3">
      <i className={`${icon} text-emerald-400 text-xl`} />
    </div>
    <p className="text-[13px] font-700 text-gray-700 mb-1">{title}</p>
    <p className="text-[12px] text-gray-400 max-w-xs">{desc}</p>
  </div>
);

// ── Suggestion Card (block) ──────────────────────────────────────────────────
interface CardProps {
  suggestion: Suggestion;
  onVote: (id: string) => void;
  onDownvote: (id: string) => void;
  onOpen: () => void;
  isOwn?: boolean;
  showModNotes?: boolean;
}

const SuggestionCard = ({ suggestion: s, onVote, onDownvote, onOpen, isOwn = false, showModNotes = false }: CardProps) => {
  const st  = SUGGESTION_STATUS_META[s.status];
  const cat = SUGGESTION_CATEGORIES.find(c => c.value === s.category);
  const isVotable = ['em_analise', 'aprovada', 'planejada', 'em_desenvolvimento', 'concluida'].includes(s.status);

  return (
    <div
      onClick={onOpen}
      className={`relative flex flex-col bg-white border rounded-2xl p-5 transition-all hover:border-gray-300 cursor-pointer group ${
        s.user_has_voted ? 'border-emerald-200' : s.user_has_downvoted ? 'border-red-100' : 'border-gray-100'
      }`}
    >
      {/* Category icon + status */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
          <i className={`${cat?.icon ?? 'ri-lightbulb-line'} text-emerald-500 text-base`} />
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-600 flex items-center gap-1 shrink-0 mt-0.5 ${st.cls}`}>
          <i className={`${st.icon} text-[9px]`} />
          {st.label}
        </span>
      </div>

      {/* Title */}
      <p className="text-[13px] font-700 text-gray-900 leading-snug mb-1.5 line-clamp-2 group-hover:text-emerald-700 transition-colors">
        {s.title}
      </p>

      {/* Description */}
      <p className="text-[12px] text-gray-500 leading-relaxed line-clamp-3 flex-1">
        {s.description}
      </p>

      {/* Mod notes */}
      {showModNotes && isOwn && s.moderation_notes && (
        <div className="mt-3 px-2.5 py-2 bg-gray-50 border border-gray-100 rounded-xl">
          <p className="text-[10px] font-600 text-gray-400 mb-0.5">Observação do time</p>
          <p className="text-[11px] text-gray-600 line-clamp-2">{s.moderation_notes}</p>
        </div>
      )}

      {/* Footer */}
      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between gap-2">
        <div className="flex flex-col gap-0.5 min-w-0">
          {cat && (
            <span className="flex items-center gap-1 text-[11px] text-gray-400">
              <i className={`${cat.icon} text-[10px]`} />
              {cat.label}
            </span>
          )}
          <span className="text-[10px] text-gray-300">{fmtDate(s.created_at)}</span>
        </div>

        {/* Vote buttons — stop propagation so clicks don't re-open modal */}
        <div className="flex items-center gap-1.5 shrink-0" onClick={e => e.stopPropagation()}>
          {isVotable ? (
            <>
              <button
                onClick={() => onVote(s.id)}
                title="Gostei"
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-[12px] font-700 cursor-pointer transition-all whitespace-nowrap ${
                  s.user_has_voted ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-emerald-50 hover:text-emerald-600'
                }`}
              >
                <i className={`${s.user_has_voted ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'} text-[12px]`} />
                {s.votes_count}
              </button>
              <button
                onClick={() => onDownvote(s.id)}
                title="Não gostei"
                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-xl text-[12px] font-700 cursor-pointer transition-all whitespace-nowrap ${
                  s.user_has_downvoted ? 'bg-red-400 text-white' : 'bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-500'
                }`}
              >
                <i className={`${s.user_has_downvoted ? 'ri-thumb-down-fill' : 'ri-thumb-down-line'} text-[12px]`} />
                {s.downvotes_count ?? 0}
              </button>
            </>
          ) : (
            <div className="flex items-center gap-1 px-2.5 py-1.5 bg-gray-50 rounded-xl text-[12px] text-gray-400">
              <i className="ri-thumb-up-line text-[12px]" />
              {s.votes_count}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Suggestion Detail Modal ──────────────────────────────────────────────────
interface DetailModalProps {
  suggestion: Suggestion;
  onClose: () => void;
  onVote: (id: string) => void;
  onDownvote: (id: string) => void;
}

const SuggestionDetailModal = ({ suggestion: s, onClose, onVote, onDownvote }: DetailModalProps) => {
  const st  = SUGGESTION_STATUS_META[s.status];
  const cat = SUGGESTION_CATEGORIES.find(c => c.value === s.category);
  const isVotable = ['em_analise', 'aprovada', 'planejada', 'em_desenvolvimento', 'concluida'].includes(s.status);
  const currentStep = getStatusProgress(s.status);

  // Build timeline entries
  const timelineEvents = [
    { label: 'Sugestão enviada', date: s.created_at, done: true, icon: 'ri-send-plane-line', color: 'bg-emerald-500' },
    ...STATUS_FLOW.map((status, idx) => {
      const meta = SUGGESTION_STATUS_META[status];
      const isDone = idx <= currentStep && s.status !== 'recusada';
      const isCurrent = idx === currentStep && s.status !== 'recusada';
      return {
        label: meta.label,
        date: isCurrent ? s.updated_at : null,
        done: isDone,
        icon: meta.icon,
        color: isCurrent ? 'bg-emerald-500' : isDone ? 'bg-emerald-400' : 'bg-gray-200',
        isCurrent,
      };
    }),
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-5 border-b border-gray-100">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="w-11 h-11 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className={`${cat?.icon ?? 'ri-lightbulb-line'} text-emerald-500 text-lg`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  {cat && (
                    <span className="text-[11px] text-gray-400 font-500">{cat.label}</span>
                  )}
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-600 flex items-center gap-1 ${st.cls}`}>
                    <i className={`${st.icon} text-[9px]`} />
                    {st.label}
                  </span>
                </div>
                <h2 className="text-[16px] font-700 text-gray-900 leading-snug">{s.title}</h2>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-gray-100 text-gray-400 cursor-pointer transition-colors flex-shrink-0"
            >
              <i className="ri-close-line text-base" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-6">
          {/* Full description */}
          <div>
            <h3 className="text-[11px] font-700 text-gray-400 uppercase tracking-wide mb-2">Descrição completa</h3>
            <p className="text-[14px] text-gray-700 leading-relaxed">{s.description}</p>
          </div>

          {/* Moderation notes */}
          {s.moderation_notes && (
            <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl">
              <div className="flex items-center gap-1.5 mb-1.5">
                <i className="ri-team-line text-amber-600 text-[13px]" />
                <span className="text-[11px] font-700 text-amber-700">Nota do time</span>
              </div>
              <p className="text-[13px] text-amber-800 leading-relaxed">{s.moderation_notes}</p>
            </div>
          )}

          {/* Status timeline */}
          <div>
            <h3 className="text-[11px] font-700 text-gray-400 uppercase tracking-wide mb-4">Histórico de status</h3>
            <div className="relative">
              {timelineEvents.map((ev, idx) => (
                <div key={idx} className="flex gap-3 pb-5 last:pb-0">
                  {/* Line + dot */}
                  <div className="flex flex-col items-center">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${ev.done ? (ev.isCurrent ? 'bg-emerald-500' : 'bg-emerald-400') : 'bg-gray-100'}`}>
                      <i className={`${ev.icon} text-[11px] ${ev.done ? 'text-white' : 'text-gray-400'}`} />
                    </div>
                    {idx < timelineEvents.length - 1 && (
                      <div className={`w-0.5 flex-1 mt-1 ${ev.done ? 'bg-emerald-200' : 'bg-gray-100'}`} style={{ minHeight: '16px' }} />
                    )}
                  </div>
                  {/* Content */}
                  <div className="flex-1 min-w-0 pt-0.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-[13px] font-600 leading-snug ${ev.done ? 'text-gray-800' : 'text-gray-400'}`}>
                        {ev.label}
                        {ev.isCurrent && (
                          <span className="ml-2 text-[10px] font-600 text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">
                            atual
                          </span>
                        )}
                      </span>
                      {ev.date && (
                        <span className="text-[11px] text-gray-400 whitespace-nowrap">{fmtDatetime(ev.date)}</span>
                      )}
                    </div>
                    {!ev.done && !ev.isCurrent && (
                      <p className="text-[11px] text-gray-400 mt-0.5">Aguardando...</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Meta info */}
          <div className="flex items-center gap-4 pt-2 border-t border-gray-100">
            <div className="flex items-center gap-1.5 text-[12px] text-gray-400">
              <i className="ri-calendar-line text-[12px]" />
              <span>Criada em {fmtDate(s.created_at)}</span>
            </div>
            <div className="flex items-center gap-1.5 text-[12px] text-gray-400">
              <i className="ri-refresh-line text-[12px]" />
              <span>Atualizada {fmtDate(s.updated_at)}</span>
            </div>
          </div>
        </div>

        {/* Footer: vote actions */}
        {isVotable && (
          <div className="px-6 pb-5 flex items-center gap-3">
            <p className="text-[12px] text-gray-500 flex-1">Esta sugestão é útil para você?</p>
            <button
              onClick={() => onVote(s.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-700 cursor-pointer transition-all whitespace-nowrap ${
                s.user_has_voted ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-emerald-50 hover:text-emerald-600'
              }`}
            >
              <i className={`${s.user_has_voted ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'} text-sm`} />
              {s.user_has_voted ? 'Votou!' : 'Votar'}
              <span className="text-[11px] opacity-80">({s.votes_count})</span>
            </button>
            <button
              onClick={() => onDownvote(s.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-[13px] font-700 cursor-pointer transition-all whitespace-nowrap ${
                s.user_has_downvoted ? 'bg-red-400 text-white' : 'bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-500'
              }`}
            >
              <i className={`${s.user_has_downvoted ? 'ri-thumb-down-fill' : 'ri-thumb-down-line'} text-sm`} />
              <span className="text-[11px] opacity-80">({s.downvotes_count ?? 0})</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Form Modal ───────────────────────────────────────────────────────────────
interface FormModalProps {
  onClose: () => void;
  onCreate: (v: { title: string; description: string; category: SuggestionCategory }) => Promise<{ success: boolean; error?: string }>;
}

const SuggestionFormModal = ({ onClose, onCreate }: FormModalProps) => {
  const [title, setTitle]             = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory]       = useState<SuggestionCategory>('funcionalidade');
  const [submitting, setSubmitting]   = useState(false);
  const [error, setError]             = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) return;
    setSubmitting(true);
    setError(null);
    const res = await onCreate({ title: title.trim(), description: description.trim(), category });
    setSubmitting(false);
    if (res.success) onClose();
    else setError(res.error ?? 'Erro ao enviar.');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-lg overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-[15px] font-700 text-gray-900">Nova sugestão</h3>
            <p className="text-[11px] text-gray-400">Publicada automaticamente para todos os usuários</p>
          </div>
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
            <i className="ri-close-line" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
          <div>
            <label className="text-[11px] font-600 text-gray-500 uppercase tracking-wide block mb-2">Categoria *</label>
            <div className="grid grid-cols-3 gap-1.5">
              {SUGGESTION_CATEGORIES.map(cat => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => setCategory(cat.value)}
                  className={`flex items-center gap-1.5 px-2.5 py-2 rounded-xl border text-[11px] font-500 cursor-pointer transition-colors whitespace-nowrap ${
                    category === cat.value ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <i className={`${cat.icon} text-[12px]`} />{cat.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[11px] font-600 text-gray-500 uppercase tracking-wide block mb-1.5">Título *</label>
            <input
              type="text" value={title} onChange={e => setTitle(e.target.value)} maxLength={120} required
              placeholder="Resumo da sua sugestão em uma linha"
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-[13px] text-gray-800 focus:outline-none focus:border-emerald-400"
            />
          </div>

          <div>
            <label className="text-[11px] font-600 text-gray-500 uppercase tracking-wide block mb-1.5">Descrição *</label>
            <textarea
              value={description} onChange={e => setDescription(e.target.value)} maxLength={500} required rows={3}
              placeholder="Explique com detalhes: qual problema resolve, como funcionaria..."
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-[13px] text-gray-800 focus:outline-none focus:border-emerald-400 resize-none"
            />
            <p className="text-[10px] text-gray-400 text-right mt-0.5">{description.length}/500</p>
          </div>

          {error && (
            <div className="px-3 py-2 bg-red-50 border border-red-100 rounded-xl text-[12px] text-red-600">
              <i className="ri-error-warning-line mr-1" />{error}
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-600 rounded-xl text-[13px] cursor-pointer hover:bg-gray-50 whitespace-nowrap">
              Cancelar
            </button>
            <button
              type="submit" disabled={submitting || !title.trim() || !description.trim()}
              className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white font-600 rounded-xl text-[13px] cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              {submitting ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-send-plane-line" />}
              {submitting ? 'Enviando...' : 'Enviar sugestão'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
